import { useEffect, useState, useCallback } from 'react';
import {
  registerServiceWorker,
  requestNotificationPermission,
  subscribeToPushNotifications as subscribePush,
  unsubscribeFromPushNotifications as unsubscribePush,
  getPushSubscription,
  subscriptionToJSON,
  isPushNotificationSupported,
  isServiceWorkerReady,
} from '../utils/pushNotifications';
import { apiService } from '../services/api';

interface UsePushNotificationsOptions {
  enabled?: boolean;
  orderId?: number; // For anonymous order notifications
}

interface PushNotificationState {
  isSupported: boolean;
  isRegistered: boolean;
  permission: NotificationPermission;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for managing push notifications
 */
export function usePushNotifications(options: UsePushNotificationsOptions = {}) {
  const { enabled = true, orderId } = options;
  
  const [state, setState] = useState<PushNotificationState>({
    isSupported: isPushNotificationSupported(),
    isRegistered: false,
    permission: 'default' as NotificationPermission,
    isLoading: false,
    error: null,
  });

  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [vapidPublicKey, setVapidPublicKey] = useState<string | null>(null);

  /**
   * Validate VAPID public key against expected key from environment
   * This provides security by ensuring the backend returns the correct key
   */
  const validateVapidKey = useCallback((keyFromBackend: string): boolean => {
    const expectedKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;

    if (!keyFromBackend || typeof keyFromBackend !== 'string') {
      console.error('❌ VAPID public key missing or invalid from backend.');
      return false;
    }
    
    // If no expected key is configured
    if (!expectedKey) {
      if (import.meta.env.DEV) {
        // In development, allow but warn
        console.warn('⚠️ VITE_VAPID_PUBLIC_KEY not set in environment. Key validation skipped.');
        return true;
      } else {
        // In production, require the key for security
        console.error('❌ VITE_VAPID_PUBLIC_KEY not set in production. Security validation required.');
        return false;
      }
    }
    
    // Normalize keys for comparison (remove whitespace, convert to same format)
    const normalizeKey = (key: string) => key.trim().replace(/\s+/g, '');
    const normalizedBackend = normalizeKey(keyFromBackend);
    const normalizedExpected = normalizeKey(expectedKey);
    
    // Compare keys
    if (normalizedBackend !== normalizedExpected) {
      console.error('❌ VAPID key validation failed!', {
        expected: normalizedExpected.substring(0, 20) + '...',
        received: normalizedBackend.substring(0, 20) + '...',
      });
      return false;
    }
    
    return true;
  }, []);

  /**
   * Fetch VAPID public key from backend and validate it
   */
  const fetchVapidKey = useCallback(async () => {
    try {
      const response = await apiService.getVapidPublicKey();
      const keyFromBackend = response?.public_key;

      if (!keyFromBackend || typeof keyFromBackend !== 'string') {
        const errorMsg = 'VAPID public key not provided by backend';
        console.error('❌', errorMsg, { response });
        setState((prev) => ({
          ...prev,
          error: errorMsg,
        }));
        return null;
      }
      
      // Validate the key against expected key from environment
      if (!validateVapidKey(keyFromBackend)) {
        const errorMsg = 'VAPID key validation failed. Security check failed.';
        console.error('❌', errorMsg);
        setState((prev) => ({
          ...prev,
          error: errorMsg,
        }));
        return null;
      }
      
      setVapidPublicKey(keyFromBackend);
      console.log('✅ VAPID key validated successfully');
      return keyFromBackend;
    } catch (error: any) {
      console.error('❌ Error fetching VAPID public key:', error);
      setState((prev) => ({
        ...prev,
        error: 'Failed to fetch VAPID key from backend',
      }));
      return null;
    }
  }, [validateVapidKey]);

  /**
   * Register for push notifications
   */
  const register = useCallback(async () => {
    if (!state.isSupported || !enabled) {
      return;
    }

    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      // Fetch VAPID public key from backend if not already fetched
      let key = vapidPublicKey;
      if (!key) {
        key = await fetchVapidKey();
        if (!key) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: 'VAPID key not available',
          }));
          return;
        }
      }

      // Check notification permission
      const permission = await requestNotificationPermission();
      setState((prev) => ({ ...prev, permission }));

      if (permission !== 'granted') {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: 'Notification permission denied',
        }));
        return;
      }

      // Register service worker
      let swRegistration = registration;
      if (!swRegistration) {
        swRegistration = await registerServiceWorker();
        if (!swRegistration) {
          throw new Error('Failed to register service worker');
        }
        setRegistration(swRegistration);
      }

      // Subscribe to push notifications
      const subscription = await subscribePush(
        swRegistration,
        key
      );

      if (!subscription) {
        throw new Error('Failed to subscribe to push notifications');
      }

      // Send subscription to backend
      const subscriptionData = subscriptionToJSON(subscription);
      await apiService.subscribeToPushNotifications({
        ...subscriptionData,
        order_id: orderId || undefined, // Include orderId for anonymous users
      });

      setState((prev) => ({
        ...prev,
        isRegistered: true,
        isLoading: false,
        error: null,
      }));

      console.log('✅ Push notifications registered successfully');
    } catch (error: any) {
      console.error('❌ Error registering push notifications:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Failed to register push notifications',
      }));
    }
  }, [state.isSupported, enabled, vapidPublicKey, registration, fetchVapidKey]);

  /**
   * Unregister from push notifications
   */
  const unregister = useCallback(async () => {
    if (!registration) {
      return;
    }

    try {
      // Get current subscription
      const subscription = await getPushSubscription(registration);
      if (!subscription) {
        setState((prev) => ({
          ...prev,
          isRegistered: false,
        }));
        return;
      }

      // Unsubscribe from push notifications
      await unsubscribePush(registration);
      
      // Remove subscription from backend
      await apiService.unsubscribeFromPushNotifications({
        endpoint: subscription.endpoint,
        order_id: orderId || undefined, // Include orderId for anonymous users
      });

      setState((prev) => ({
        ...prev,
        isRegistered: false,
      }));

      console.log('✅ Push notifications unregistered');
    } catch (error: any) {
      console.error('❌ Error unregistering push notifications:', error);
      setState((prev) => ({
        ...prev,
        error: error.message || 'Failed to unregister push notifications',
      }));
    }
  }, [registration]);

  /**
   * Check current subscription status
   */
  const checkSubscription = useCallback(async () => {
    if (!registration) {
      return;
    }

    try {
      const subscription = await getPushSubscription(registration);
      setState((prev) => ({
        ...prev,
        isRegistered: !!subscription,
        permission: Notification.permission as NotificationPermission,
      }));
    } catch (error) {
      console.error('❌ Error checking subscription:', error);
    }
  }, [registration]);

  // Initialize on mount
  useEffect(() => {
    if (!state.isSupported || !enabled) {
      return;
    }

    const initialize = async () => {
      setState((prev) => ({ ...prev, permission: Notification.permission as NotificationPermission }));
      
      const ready = await isServiceWorkerReady();
      if (ready) {
        const swRegistration = await navigator.serviceWorker.ready;
        setRegistration(swRegistration);
        await checkSubscription();
      }
    };

    initialize();
  }, [state.isSupported, enabled, checkSubscription]);

  return {
    ...state,
    register,
    unregister,
    checkSubscription,
  };
}
